import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { authAPI } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const ROLES = ['admin', 'technicien', 'viewer'];
const ROLE_LABELS = { admin: 'Admin', technicien: 'Technicien', viewer: 'Lecture' };
const ROLE_COLORS = { admin: 'var(--red)', technicien: 'var(--blue)', viewer: 'var(--text-muted)' };
const EMPTY = { nom: '', email: '', password: '', role: 'technicien' };

const isMobile = () => window.innerWidth <= 768;

export default function Utilisateurs() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mobile, setMobile] = useState(isMobile());

  useEffect(() => {
    const handleResize = () => setMobile(isMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const load = () => {
    setLoading(true);
    authAPI.getUsers().then(r => setUsers(r.data)).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openCreate = () => { setForm(EMPTY); setCurrent(null); setModal('form'); };
  const openEdit = (u) => { setForm({ nom: u.nom, email: u.email, password: '', role: u.role }); setCurrent(u); setModal('form'); };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (current && !payload.password) delete payload.password;
      if (current) {
        await authAPI.updateUser(current.id || current._id, payload);
        toast.success('Utilisateur mis à jour');
      } else {
        await authAPI.register(payload);
        toast.success('Utilisateur créé');
      }
      setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Supprimer ${u.nom} ?`)) return;
    try {
      await authAPI.deleteUser(u.id || u._id);
      toast.success('Supprimé');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Erreur'); }
  };

  const handleToggle = async (u) => {
    try {
      await authAPI.updateUser(u.id || u._id, { actif: !u.actif });
      toast.success(u.actif ? 'Compte désactivé' : 'Compte activé');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Erreur'); }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try { return format(new Date(d), 'dd/MM/yyyy HH:mm', { locale: fr }); }
    catch { return '—'; }
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div className="page-title">Utilisateurs</div>
          <div className="page-subtitle">Gestion des accès et permissions</div>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Ajouter</button>
      </div>

      <div className="table-container">
        <div className="table-toolbar">
          <span className="toolbar-title">{users.length} compte(s)</span>
        </div>

        {loading ? (
          <div className="loading"><div className="spinner"></div>Chargement...</div>
        ) : mobile ? (
          /* MOBILE — Cards */
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {users.map(u => (
              <div key={u.id || u._id} style={{
                padding: '14px 16px',
                borderBottom: '1px solid var(--border-light)',
                opacity: u.actif ? 1 : 0.5,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: ROLE_COLORS[u.role], color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 16, flexShrink: 0,
                  }}>
                    {u.nom.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {u.nom}
                      {(u.id === me?.id || u._id === me?._id) && <span style={{ fontSize: 10, color: 'var(--blue)', marginLeft: 6, fontWeight: 400 }}>VOUS</span>}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{u.email}</div>
                  </div>
                  <span style={{
                    padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                    background: `${ROLE_COLORS[u.role]}15`, color: ROLE_COLORS[u.role],
                  }}>
                    {ROLE_LABELS[u.role]}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-sm btn-outline" onClick={() => openEdit(u)} style={{ flex: 1, justifyContent: 'center' }}>✎ Modifier</button>
                  <button className={`btn btn-sm ${u.actif ? 'btn-amber' : 'btn-green'}`}
                    onClick={() => handleToggle(u)}
                    disabled={u.id === me?.id || u._id === me?._id}>
                    {u.actif ? '⊘' : '◉'}
                  </button>
                  <button className="btn btn-sm btn-danger"
                    onClick={() => handleDelete(u)}
                    disabled={u.id === me?.id || u._id === me?._id}>
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* DESKTOP — Table */
          <table>
            <thead>
              <tr>
                <th>Nom</th><th>Email</th><th>Rôle</th><th>Statut</th><th>Dernière connexion</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id || u._id} style={{ opacity: u.actif ? 1 : 0.5 }}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%',
                        background: ROLE_COLORS[u.role], color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 600, fontSize: 13, flexShrink: 0,
                      }}>
                        {u.nom.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600 }}>{u.nom}</div>
                        {(u.id === me?.id || u._id === me?._id) && (
                          <div style={{ fontSize: 10, color: 'var(--blue)' }}>VOUS</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{u.email}</td>
                  <td>
                    <span style={{
                      padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                      background: `${ROLE_COLORS[u.role]}15`, color: ROLE_COLORS[u.role],
                    }}>
                      {ROLE_LABELS[u.role]}
                    </span>
                  </td>
                  <td>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: u.actif ? 'var(--green)' : 'var(--text-muted)' }} />
                      {u.actif ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{formatDate(u.last_login)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-sm btn-outline" onClick={() => openEdit(u)}>✎</button>
                      <button className={`btn btn-sm ${u.actif ? 'btn-amber' : 'btn-green'}`}
                        onClick={() => handleToggle(u)}
                        disabled={u.id === me?.id || u._id === me?._id}>
                        {u.actif ? '⊘' : '◉'}
                      </button>
                      <button className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(u)}
                        disabled={u.id === me?.id || u._id === me?._id}>
                        ✕
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Légende */}
      <div style={{ marginTop: 12, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {[
          { role: 'admin', desc: 'Accès total, gère les utilisateurs' },
          { role: 'technicien', desc: 'Crée et modifie équipements et mouvements' },
          { role: 'viewer', desc: 'Lecture seule' },
        ].map(r => (
          <div key={r.role} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)' }}>
            <span style={{ color: ROLE_COLORS[r.role], fontWeight: 600 }}>{ROLE_LABELS[r.role]}</span>
            <span>—</span>
            <span>{r.desc}</span>
          </div>
        ))}
      </div>

      {/* Modal */}
      {modal === 'form' && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-header">
              <span className="modal-title">{current ? 'Modifier utilisateur' : 'Nouvel utilisateur'}</span>
              <button className="btn btn-sm btn-danger btn-icon" onClick={() => setModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-group">
                  <label className="form-label">Nom complet *</label>
                  <input className="input" value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} placeholder="Prénom Nom" />
                </div>
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input className="input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@exemple.com" />
                </div>
                <div className="form-group">
                  <label className="form-label">{current ? 'Nouveau mot de passe (laisser vide = inchangé)' : 'Mot de passe *'}</label>
                  <input className="input" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder={current ? 'Laisser vide pour ne pas changer' : 'Min. 6 caractères'} />
                </div>
                <div className="form-group">
                  <label className="form-label">Rôle</label>
                  <select className="select" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                    {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setModal(null)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? '...' : 'Enregistrer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
